// src/auth/AuthModal.jsx
// Editorial-styled login / sign-up modal for the blog members area.
// Change from original: name field is now REQUIRED on signup (was optional).
import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import * as dialog from "@zag-js/dialog";
import { Portal, normalizeProps, useMachine } from "@zag-js/react";
import { useAuth } from "./AuthContext";

const APPLE_DEFERRED = true;

function GoogleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09a6.97 6.97 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function AppleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M16.365 1.43c0 1.14-.41 2.18-1.23 3.02-.79.85-2.07 1.5-3.13 1.42-.13-1.1.4-2.24 1.16-3.02.85-.91 2.27-1.59 3.2-1.62.01.07.01.13.01.2zM20.95 17.16c-.55 1.27-.82 1.84-1.53 2.96-1 1.55-2.4 3.49-4.14 3.5-1.55.02-1.94-1.01-4.05-1-2.1.01-2.53 1.02-4.08 1-1.74-.02-3.07-1.77-4.06-3.32-2.79-4.32-3.08-9.39-1.36-12.08C2.95 6.4 4.91 5.27 6.76 5.27c1.88 0 3.07 1.03 4.62 1.03 1.51 0 2.43-1.03 4.61-1.03 1.65 0 3.39.9 4.64 2.45-4.08 2.24-3.41 8.09.32 9.44z" />
    </svg>
  );
}

export default function AuthModal({ open, onClose, defaultMode = "login", title, next: returnTo }) {
  const { login, signup } = useAuth();
  const service = useMachine(dialog.machine, {
    id: "blog-auth-modal",
    open,
    onOpenChange: ({ open: nextOpen }) => {
      if (!nextOpen) onClose();
    },
  });
  const api = dialog.connect(service, normalizeProps);
  const [mode,     setMode]     = useState(defaultMode);
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");
  const [error,    setError]    = useState("");
  const [busy,     setBusy]     = useState(false);

  useEffect(() => { setMode(defaultMode); }, [defaultMode, open]);

  useEffect(() => {
    if (!open) { setError(""); setBusy(false); }
  }, [open]);

  if (!api.open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    // Extra client-side guard for name on signup
    if (mode === "signup" && !name.trim()) {
      setError("Please enter your name.");
      return;
    }

    setBusy(true);
    const fn  = mode === "signup" ? signup : login;
    const res = await fn({ email, password, name });
    setBusy(false);
    if (res.ok) { onClose(res.user); return; }
    setError(res.error || "Something went wrong.");
  }

  function startGoogle() {
    const next = returnTo || window.location.pathname + window.location.search;
    window.location.href = `/api/auth/google?next=${encodeURIComponent(next)}`;
  }

  return (
    <Portal>
      <div {...api.getBackdropProps()} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }} />
      <div {...api.getPositionerProps()} style={{ position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div
        {...api.getContentProps()}
        className="blog-theme"
        data-blog-theme={typeof window !== "undefined" && localStorage.getItem("mc_blog_theme") === "dark" ? "dark" : "light"}
        data-testid="auth-modal"
        style={{
          width:      "100%",
          maxWidth:   420,
          background: "var(--blog-bg-primary)",
          border:     "0.5px solid var(--blog-border-primary)",
          borderRadius: 12,
          padding:    28,
          position:   "relative",
          color:      "var(--blog-text-primary)"
        }}
      >
        <button
          {...api.getCloseTriggerProps()}
          data-testid="auth-modal-close"
          type="button"
          aria-label="Close"
          style={{
            position:   "absolute",
            top:        12,
            right:      12,
            background: "transparent",
            border:     "none",
            color:      "var(--blog-text-tertiary)",
            cursor:     "pointer",
            padding:    6
          }}
        >
          <X className="w-4 h-4" />
        </button>

        <div
          className="uppercase mb-2"
          style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-accent-teal)" }}
        >
          {title || "Members"}
        </div>
        <h2
          {...api.getTitleProps()}
          className="font-display font-medium"
          style={{ fontSize: 26, lineHeight: 1.15, letterSpacing: "-0.01em" }}
        >
          {mode === "signup" ? "Create your free account" : "Welcome back"}
        </h2>
        <p {...api.getDescriptionProps()} style={{ fontSize: 12, color: "var(--blog-text-tertiary)", marginTop: 6 }}>
          {mode === "signup"
            ? "Create an account to read members-only essays and keep your name attached to comments."
            : "Sign in to unlock the members-only essays."}
        </p>

        <div style={{ display: "grid", gap: 8, marginTop: 22 }}>
          <button
            type="button"
            data-testid="auth-google-button"
            onClick={startGoogle}
            style={{
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              gap:            10,
              padding:        "11px 14px",
              borderRadius:   8,
              border:         "0.5px solid var(--blog-border-primary)",
              background:     "var(--blog-bg-secondary)",
              color:          "var(--blog-text-primary)",
              fontSize:       13,
              fontWeight:     500,
              cursor:         "pointer"
            }}
          >
            <GoogleIcon /> Continue with Google
          </button>
          <button
            type="button"
            disabled={APPLE_DEFERRED}
            data-testid="auth-apple-button"
            title={APPLE_DEFERRED ? "Apple Sign-in  -  coming soon" : ""}
            style={{
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              gap:            10,
              padding:        "11px 14px",
              borderRadius:   8,
              border:         "0.5px solid var(--blog-border-primary)",
              background:     "var(--blog-bg-secondary)",
              color:          "var(--blog-text-tertiary)",
              fontSize:       13,
              fontWeight:     500,
              cursor:         APPLE_DEFERRED ? "not-allowed" : "pointer",
              opacity:        APPLE_DEFERRED ? 0.55 : 1
            }}
          >
            <AppleIcon /> Continue with Apple
            {APPLE_DEFERRED && (
              <span style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", marginLeft: 4 }}>
                Soon
              </span>
            )}
          </button>
        </div>

        <div
          style={{
            display:       "flex",
            alignItems:    "center",
            gap:           10,
            margin:        "20px 0 18px",
            fontSize:      10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color:         "var(--blog-text-tertiary)"
          }}
        >
          <div style={{ flex: 1, height: "0.5px", background: "var(--blog-border-tertiary)" }} />
          or with email
          <div style={{ flex: 1, height: "0.5px", background: "var(--blog-border-tertiary)" }} />
        </div>

        <form onSubmit={handleSubmit} data-testid="auth-email-form" style={{ display: "grid", gap: 10 }}>
          {mode === "signup" && (
            <>
              {/* Name is REQUIRED  -  used for personalised greetings, comments & email verification */}
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                required
                minLength={2}
                style={inputStyle()}
              />
              <p style={{ fontSize: 10, color: "var(--blog-text-tertiary)", margin: "-4px 0 0", letterSpacing: "0.02em" }}>
                Used to personalise your experience and for comment attribution.
              </p>
            </>
          )}
          <input
            data-testid="auth-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoComplete="email"
            required
            style={inputStyle()}
          />
          <input
            data-testid="auth-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === "signup" ? "Password (min 8 chars)" : "Password"}
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            minLength={mode === "signup" ? 8 : undefined}
            required
            style={inputStyle()}
          />
          {error && (
            <div data-testid="auth-error" style={{ fontSize: 11, color: "#dc2626", lineHeight: 1.5 }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-button"
            style={{
              marginTop:      6,
              padding:        "11px 14px",
              borderRadius:   8,
              border:         "none",
              background:     "var(--blog-text-primary)",
              color:          "var(--blog-bg-primary)",
              fontSize:       12,
              fontWeight:     500,
              letterSpacing:  "0.04em",
              cursor:         busy ? "wait" : "pointer",
              opacity:        busy ? 0.7 : 1,
              display:        "inline-flex",
              alignItems:     "center",
              justifyContent: "center",
              gap:            8
            }}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>

        <div style={{ marginTop: 16, fontSize: 11, color: "var(--blog-text-tertiary)" }}>
          {mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button
                onClick={() => { setMode("login"); setError(""); }}
                data-testid="auth-switch-login"
                style={linkBtn()}
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              New here?{" "}
              <button
                onClick={() => { setMode("signup"); setError(""); }}
                data-testid="auth-switch-signup"
                style={linkBtn()}
              >
                Create an account
              </button>
            </>
          )}
        </div>
      </div>
      </div>
    </Portal>
  );
}

function inputStyle() {
  return {
    padding:     "11px 13px",
    borderRadius: 8,
    border:      "0.5px solid var(--blog-border-primary)",
    background:  "var(--blog-bg-primary)",
    color:       "var(--blog-text-primary)",
    fontSize:    13,
    outline:     "none",
    fontFamily:  "inherit"
  };
}

function linkBtn() {
  return {
    background:         "transparent",
    border:             "none",
    color:              "var(--blog-accent-teal)",
    fontSize:           11,
    cursor:             "pointer",
    padding:            0,
    textDecoration:     "underline",
    textUnderlineOffset: 2
  };
}
